import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { ConditionParams } from "@multiweb/clients/dataReader";
import { bridge } from "@multiweb/reactCore/bridge";
import { EMPTY_ARRAY, stringToFlagColorValue } from "@multiweb/reactUtils";
import { IconCode, IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import {
    ConditionOperator,
    ExtendedPropertiesType,
    IOrderBy,
    IWorkflowMenuItem,
    OrderType,
} from "@multiweb/types/multiweb";
import {
    FetchingState,
    FlagColor,
    IAppState,
    ReactAction,
    ReactActionId,
    RecordType,
    TypedObject,
} from "@multiweb/types/react";
import { getDefaultCondition } from "./constants";
import {
    ActionSelectorParams,
    AttributeSelectorParams,
    IDataSourceAttribute,
    IDataSourceConfig,
    IDataSourceState,
    RecordAttributeSelectorParams,
    RecordSelectorParams,
    WorkflowActionSelectorParams,
} from "./types";

const { combineConditionsArray } = bridge;

/**
 * Získá z globálního stavu instanci patřící DataSource podle `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getDataSourceInstance: Selector<IAppState, Readonly<IDataSourceState> | undefined, string> = (
    state: IAppState,
    dataSourceKey: string,
): IDataSourceState => state.core.dataSource[dataSourceKey];

/**
 * Získá **recordIdKey** pro DataSource instanci získanou pomocí `dataSourceKey`.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getRecordIdKey: Selector<IAppState, Readonly<string> | undefined, string> = (state, dataSourceKey) =>
    getConfig(state, dataSourceKey)?.recordIdKey;

/**
 * Získá **objekt naplněný daty z MultiWebu** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getRecords: Selector<IAppState, TypedObject<RecordType> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.data.records;

/**
 * Získá **record** pro daný identifikátor z DataSource instance získané pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param recordSelectorParams Parametry určující instanci DataSource a idetifikátor pro record.
 */
export const getRecordById: Selector<IAppState, RecordType | undefined, RecordSelectorParams> = (
    state,
    { dataSourceKey, recordId },
) => getRecords(state, dataSourceKey)?.[recordId];

/**
 * Získá hodnotu ze záznamu.
 *
 * @param state
 * @param recordAttributeSelectorParams Identifikátory pro identifikaci záznamu a jeho atributu.
 */
export const getRecordValueById: Selector<IAppState, any, RecordAttributeSelectorParams> = (
    state,
    { dataSourceKey, recordId, attributeName },
) => getRecordById(state, { dataSourceKey, recordId })?.[attributeName].value;

/**
 * Získá informaci o tom, zda je daný záznam načten.
 *
 * @param state
 * @param recordSelectorParams Parametry určující instanci DataSource a idetifikátor pro record.
 */
export const getIsRecordLoaded: Selector<IAppState, boolean, RecordSelectorParams> = (
    state,
    recordSelectorParams,
) => !!getRecordById(state, recordSelectorParams);

/**
 * Získá seřazené pole identifikátorů načtených dat.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getRecordsIds: Selector<IAppState, ReadonlyArray<string> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.data.recordsIds;

/**
 * Získá **objekt naplněný hodnotou označení pod identifikátory dat** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getSelectedRecords: Selector<IAppState, TypedObject<boolean> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.multiselect.selectedRecords;

/**
 * Získá informaci o tom, zda je záznam daný identifikátorem označen v DataSource instanci dané pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param recordSelectorParams Parametry určující instanci DataSource a idetifikátor pro record.
 */
export const isRecordSelected: Selector<IAppState, boolean | undefined, RecordSelectorParams> = (
    state,
    { dataSourceKey, recordId },
) => getSelectedRecords(state, dataSourceKey)?.[recordId];

/**
 * Získá **objekt naplněný hodnotami vybrání pod identifikátory dat** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getCheckedRecords: Selector<IAppState, TypedObject<boolean> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.multiselect.checkedRecords;

/**
 * Získá informaci o tom, zda je záznam daný identifikátorem vybrán v DataSource instanci dané pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param recordSelectorParams Parametry určující instanci DataSource a idetifikátor pro record.
 */
export const isRecordChecked: Selector<IAppState, boolean | undefined, RecordSelectorParams> = (
    state,
    { dataSourceKey, recordId },
) => getCheckedRecords(state, dataSourceKey)?.[recordId];

/**
 * Získá informaci o tom, zda je označený alespoň jeden záznam pro DataSource instanci získanou pomocí `dataSourceKey`.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const createIsAtLeastOneRecordChecked = () =>
    createSelector<IAppState, string, ReturnType<typeof getCheckedRecords>, boolean | undefined>(
        (state, dataSourceKey) => getCheckedRecords(state, dataSourceKey),
        (checkedRecords) => {
            if (!checkedRecords) {
                return undefined;
            }
            return Object.values(checkedRecords).some(record => record);
        },
    );

/**
 * Získá pole identifikátorů označených (checked) záznamů pro DataSource instanci získanou pomocí `dataSourceKey`.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const createGetCheckedRecordsIds = () =>
    createSelector<IAppState, string, ReturnType<typeof getCheckedRecords>, ReadonlyArray<string> | undefined>(
        (state, dataSourceKey) => getCheckedRecords(state, dataSourceKey),
        (checkedRecords) => {
            if (!checkedRecords) {
                return undefined;
            }
            return Object.keys(checkedRecords).filter((recordKey) => checkedRecords[recordKey]);
        },
    );

/**
 * Získá pole identifikátorů odoznačených (!checked) záznamů pro DataSource instanci získanou pomocí `dataSourceKey`.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const createGetUncheckedRecordsIds = () =>
    createSelector<IAppState, string, ReturnType<typeof getCheckedRecords>, ReadonlyArray<string> | undefined>(
        (state, dataSourceKey) => getCheckedRecords(state, dataSourceKey),
        (checkedRecords) => {
            if (!checkedRecords) {
                return undefined;
            }
            return Object.keys(checkedRecords).filter((recordKey) => !checkedRecords[recordKey]);
        },
    );

/**
 * Získá **stav načítání** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getFetchingState: Selector<IAppState, Readonly<FetchingState> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.loading.fetchingState;

/**
 * Zjistí, zda se načítají data v DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const isFetching: Selector<IAppState, Readonly<boolean> | undefined, string> = (state, dataSourceKey) => {
    const fetchingState = getFetchingState(state, dataSourceKey);
    return fetchingState ? fetchingState === FetchingState.request : undefined;
};

/**
 * Zjistí, zda jsou další data pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const hasNextItems: Selector<IAppState, Readonly<boolean> | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.loading.hasNextItems;

/**
 * Zjistí, zda DataSource obsahuje data.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const isEmpty: Selector<IAppState, Readonly<boolean | undefined>, string> = (state, dataSourceKey) => {
    const recordIds = getRecordsIds(state, dataSourceKey);
    return recordIds ? recordIds.length === 0 : undefined;
};

/**
 * Získá **config** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getConfig: Selector<IAppState, Readonly<IDataSourceConfig> | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.config;

/**
 * Získá atributy z konfigurace DataSource.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getConfigAttributes: Selector<IAppState, IDataSourceConfig["attributes"] | undefined, string> = (
    state,
    dataSourceKey,
) => getConfig(state, dataSourceKey)?.attributes;

/**
 * Získá seřazená jména atributů z konfigurace DataSource.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getConfigAttributeNames: Selector<IAppState, IDataSourceConfig["attributeNames"] | undefined, string> = (
    state,
    dataSourceKey,
) => getConfig(state, dataSourceKey)?.attributeNames;

/**
 * Získá seznam atributů, jejichž hodnoty nechceme automaticky ořezávat.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getConfigFullLengthAttributes: Selector<IAppState, IDataSourceConfig["fullLengthAttributes"] | undefined, string> = (
    state,
    dataSourceKey,
) => getConfig(state, dataSourceKey)?.fullLengthAttributes;

/**
 * Získá konkrétní atribut z konfigurace.
 *
 * @param state
 * @param attributeSelectorParams Obsahuje klíč k instanci DataSource a název atributu.
 */
export const getConfigAttributeByName: Selector<IAppState, IDataSourceAttribute | undefined, AttributeSelectorParams> = (
    state,
    { dataSourceKey, attributeName },
) => getConfigAttributes(state, dataSourceKey)?.[attributeName];

/**
 * Získá displayFormat pro konkrétní atribut.
 *
 * @param state
 * @param attributeSelectorParams Obsahuje klíč k instanci DataSource a název atributu.
 */
export const getConfigAttributeDisplayFormat: Selector<IAppState, string | undefined, AttributeSelectorParams> = (
    state,
    attributeSelectorParams,
) => getConfigAttributeByName(state, attributeSelectorParams)?.displayFormat || undefined;

/**
 * Získá seřazené názvy atributů které jsou/nejsou ikony (podle vstupního parametru)
 *
 * @param isIcon Určuje, jestli hledané atributy mají být ikona.
 */
export const createGetAttributeNames = (isIcon: boolean) =>
    createSelector<IAppState, string, ReturnType<typeof getConfigAttributeNames>, ReturnType<typeof getConfigAttributes>, ReadonlyArray<string> | undefined>(
        (state, dataSourceKey) => getConfigAttributeNames(state, dataSourceKey),
        (state, dataSourceKey) => getConfigAttributes(state, dataSourceKey),
        (attributeNames, attributes) => {
            if (!attributeNames || !attributes)
                return EMPTY_ARRAY;
            return attributeNames
                .filter(attributeName => !attributes[attributeName]?.hidden) //odfiltruje skryté atributy
                .filter(attributeName => !!attributes[attributeName].isIcon === isIcon); //vyfiltruje podle isIcon
        },
    );

/**
 * Získá index daného atributu.
 */
export const createGetAttributeNameIndex = () =>
    createSelector<IAppState, AttributeSelectorParams, ReturnType<typeof getConfigAttributeNames>, string, number | undefined>(
        (state, { dataSourceKey }) => getConfigAttributeNames(state, dataSourceKey),
        (_state, { attributeName }) => attributeName,
        (attributeNames, attributeName) =>
            attributeNames?.findIndex((attrName => attrName === attributeName)),
    );

/**
 * Získá **ObjectName** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getObjectName: Selector<IAppState, string | undefined, string> = (state, dataSourceKey) =>
    getConfig(state, dataSourceKey)?.objectName;

/**
 * Získá **attributeInfo** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getAttributesInfo: Selector<IAppState, TypedObject<IPublicAttributeInfo> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.attributeInfo;

/**
 * Získá **informace o atributu** (attributeInfo).
 *
 * @param state
 * @param attributeSelectorParams Parametry určující instanci DataSource a název atributu.
 */
export const getAttributeInfoByName: Selector<IAppState, IPublicAttributeInfo | undefined, AttributeSelectorParams> = (
    state,
    { dataSourceKey, attributeName },
) => getAttributesInfo(state, dataSourceKey)?.[attributeName];

/**
 * Získá **typ atributu** podle jeho jména (typ získá z attributeInfo).
 *
 * @param state
 * @param attributeSelectorParams Parametry určující instanci DataSource a název atributu.
 */
export const getAttributeTypeByAttributeName: Selector<IAppState, OWDataType | undefined, AttributeSelectorParams> = (
    state,
    attributeSelectorParams,
) => getAttributeInfoByName(state, attributeSelectorParams)?.type;

/**
 * Získá informaci o tom, zda je info a daném atributu načteno.
 *
 * @param state
 * @param attributeSelectorParams Parametry určující instanci DataSource a název atributu.
 */
export const getIsAttributeInfoLoaded: Selector<IAppState, boolean, AttributeSelectorParams> = (
    state,
    { dataSourceKey, attributeName },
) => !!getAttributeInfoByName(state, { dataSourceKey, attributeName });

/**
 * Získá jméno atributu na kterém je hodnota barvy pozadí.
 *
 * @param state
 * @param attributeSelectorParams Parametry určující instanci DataSource a název atributu.
 */
export const getBackgroundAttributeName: Selector<IAppState, string | undefined, AttributeSelectorParams> = (
    state,
    { dataSourceKey, attributeName },
) => getConfigAttributeByName(state, { dataSourceKey, attributeName })?.backgroundAttrName || undefined;

/**
 * Získá **barvu pozadí** atributu (podle jeho `backgroundAttributeName`).
 *
 * @param state
 * @param attributeSelectorParams Parametry určující instanci DataSource, identifikátor záznamu a název atributu.
 */
export const createGetBackgroundAttributeColor = () =>
    createSelector<
        IAppState,
        RecordAttributeSelectorParams,
        ReturnType<typeof getBackgroundAttributeName>,
        ReturnType<typeof getAttributesInfo>,
        ReturnType<typeof getRecordById>,
        FlagColor | undefined
    >(
        (state, params) => getBackgroundAttributeName(state, params),
        (state, { dataSourceKey }) => getAttributesInfo(state, dataSourceKey),
        (state, params) => getRecordById(state, params),
        (backgroundAttributeName, dataSourceAttributesInfo, record) => {
            if (
                backgroundAttributeName &&
                record &&
                dataSourceAttributesInfo?.[backgroundAttributeName]?.type === OWDataType.flags
            ) {
                return stringToFlagColorValue(record[backgroundAttributeName]?.value?.toString());
            }

            return undefined;
        },
    );

/**
 * Získá původní název atributu ze záznamu - začíná velkým písmenem
 *
 * @param state
 * @param attributeSelectorParams Parametry určující instanci DataSource a název atributu.
 */
export const getOriginalAttributeName: Selector<
    IAppState,
    string | undefined,
    AttributeSelectorParams
> = (
    state,
    { dataSourceKey, attributeName },
) => getAttributesInfo(state, dataSourceKey)?.[attributeName]?.attributeName;

/**
 * Získá **fetchingCount** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getFetchingCount: Selector<IAppState, Readonly<number> | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.loading.fetchingCount;

/**
 * Získá **startIndex** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getStartIndex: Selector<IAppState, Readonly<number> | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.loading.startIndex;

/**
 * Získá **akce** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getActions: Selector<IAppState, TypedObject<ReactAction> | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.actions.actions;

/**
 * Získá **akci** danou `actionSelectorParams`.
 *
 * @param state
 * @param actionSelectorParams Obsahuje klíč k instanci DataSource a identifikátor akce.
 */
export const getActionById: Selector<IAppState, Readonly<ReactAction> | undefined, ActionSelectorParams> = (
    state,
    { dataSourceKey, actionId },
) => getActions(state, dataSourceKey)?.[actionId];

/**
 * Získá **identifikátory akcí, které lze provést nad všemi záznamy** pro DataSource instanci získanou pomocí `dataSourceKey`.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getRecordActionsIds: Selector<IAppState, ReadonlyArray<ReactActionId> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.actions.recordActionsIds;

/**
 * Získá **hasDetailForm** pro DataSource instanci získanou pomocí `dataSourceKey`.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const hasDetailForm: Selector<IAppState, Readonly<boolean> | undefined, string> = (
    state,
    dataSourceKey,
) => getDataSourceInstance(state, dataSourceKey)?.actions.hasDetailForm;

/**
 * Získá **IconCode** pro akci danou `actionSelectorParams`.
 *
 * @param state
 * @param actionSelectorParams Obsahuje klíč k instanci DataSource a identifikátor akce.
 */
export const getActionIconCode: Selector<IAppState, Readonly<IconCode> | undefined, ActionSelectorParams> = (
    state,
    actionSelectorParams,
) => getActionById(state, actionSelectorParams)?.iconCode;

/**
 * Získá **visible** pro akci danou `actionSelectorParams`.
 *
 * @param state
 * @param actionSelectorParams Obsahuje klíč k instanci DataSource a identifikátor akce.
 */
export const getActionVisibility: Selector<IAppState, Readonly<boolean> | undefined, ActionSelectorParams> = (
    state,
    actionSelectorParams,
) => getActionById(state, actionSelectorParams)?.visible;

/**
 * Získá **locKey** pro akci danou `actionSelectorParams`.
 *
 * @param state
 * @param actionSelectorParams Obsahuje klíč k instanci DataSource a identifikátor akce.
 */
export const getActionLocKey: Selector<IAppState, Readonly<string> | undefined, ActionSelectorParams> = (
    state,
    actionSelectorParams,
) => getActionById(state, actionSelectorParams)?.locKey;

/**
 * Získá **ExtendedProperties** pro danou 'actionSelectorParams'
 * @param state
 * @param actionSelectorParams Obsahuje klíč k instanci DataSource a identifikátor akce
 * @returns extendedProperties dané akce
 */
export const getActionExtendedProperties: Selector<IAppState, Readonly<ExtendedPropertiesType> | undefined, ActionSelectorParams> = (
    state,
    actionSelectorParams,
) => getActionById(state, actionSelectorParams)?.extendedProperties;

/**
 * Získá **condition** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getCondition: Selector<IAppState, string | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.conditions?.condition;

/**
 * Získá **fulltextCondition** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getFulltextCondition: Selector<IAppState, string | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.conditions?.fulltextCondition?.condition;

/**
 * Získá **fulltextSearchString** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getFulltextSearchString: Selector<IAppState, string | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.conditions?.fulltextCondition?.searchString;

/**
 * Získá **fulltextCondition** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud **fulltextCondition** existuje a neobsahuje prázdný text, vrací pravdu.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const isFulltextConditionActive: Selector<IAppState, boolean | undefined, string> = (state, dataSourceKey) => {
    const condition = getFulltextCondition(state, dataSourceKey);
    return !condition ? undefined : condition !== "";
};

/**
 * Získá **masterFrameCondition** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getMasterFrameCondition: Selector<IAppState, string | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.conditions?.masterFrameCondition;

/**
 * Získá **conditionParams** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getConditionParams: Selector<IAppState, ConditionParams | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.conditions?.conditionParams;

/**
 * Získá podmínku načtení záznamů pro DataSource instanci získanou pomocí `dataSourceKey`.
 * Vyžívá se pouze v sáze a connectoru, proto není třeba vytvářet pomocí reselectu.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getLoadingCondition: Selector<IAppState, string | undefined, string> = (state, dataSourceKey) => {
    const recordIdKey = getRecordIdKey(state, dataSourceKey);
    if (!recordIdKey) {
        return undefined;
    }
    const condition = getCondition(state, dataSourceKey);
    const fulltextCondition = getFulltextCondition(state, dataSourceKey);
    const masterFrameCondition = getMasterFrameCondition(state, dataSourceKey);

    const conditionArray = [getDefaultCondition(recordIdKey)];
    if (condition) {
        conditionArray.push(condition);
    }
    if (fulltextCondition) {
        conditionArray.push(fulltextCondition);
    }
    if (masterFrameCondition) {
        conditionArray.push(masterFrameCondition);
    }
    return combineConditionsArray(ConditionOperator.and, conditionArray);
};

/**
 * Získá pole řazení ze store pro zvolený DataSource pomocí instancKey.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getOrderBy: Selector<IAppState, ReadonlyArray<IOrderBy> | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.orderBy;

/**
 * Získá druh řazení pro určitý attribut ve zvoleném DataSource pomocí dataSourceKey.
 *
 * @param attributeName
 */
export const createGetAttributeOrderBySelector = (attributeName: string) =>
    createSelector<IAppState, string, ReturnType<typeof getOrderBy>, Readonly<OrderType> | undefined>(
        (state, dataSourceKey) => getOrderBy(state, dataSourceKey),
        (orderBy) => {
            return orderBy?.find((orderBy) => orderBy.attributeName === attributeName)?.order;
        },
    );

/**
 * Získá **multiselectMode** pro DataSource instanci získanou pomocí `dataSourceKey`.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const getMultiselectMode: Selector<IAppState, boolean | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.multiselect.multiselectMode;

/**
 * Získá **disabledLoading** pro DataSource instanci získanou pomocí `instanceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param instanceKey Klíč k instanci DataSource.
 */
export const getDisabledLoading: Selector<IAppState, boolean | undefined, string> = (state, instanceKey) =>
    getDataSourceInstance(state, instanceKey)?.loading.disabledLoading;

/**
 * Získá **isCheckedAll** pro DataSource instanci získanou pomocí `instanceKey`.
 * Pokud pro daný klíč neexistuje instance, vrací undefined.
 *
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const isCheckedAll: Selector<IAppState, boolean | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.multiselect.isCheckedAll;

/**
 * Získá informaci, zda má záznam workflow.
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const hasWorkflow: Selector<IAppState, boolean | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.config.hasWorkflow;

/**
 * Získá workflow akce pro daný záznamy
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 * @returns workflow akce
 */
export const getWorkflowActions = () =>
    createSelector<IAppState, string, TypedObject<IWorkflowMenuItem> | undefined, ReadonlyArray<string> | undefined, IWorkflowMenuItem[] | undefined>(
        (state, dataSourceKey) => getDataSourceInstance(state, dataSourceKey)?.workflowActions.actions,
        (state, dataSourceKey) => getDataSourceInstance(state, dataSourceKey)?.workflowActions.actionsOrder,
        (actions, actionsOrder) => {
            if (actionsOrder && actions) {
                return actionsOrder.map(actionOrder => actions?.[actionOrder]);
            }
            return undefined;
        },
    );

/**
 * Získá workflow akce pro daný záznam dle workflow action id
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 * @param workflowActionId Id vyžadované workflow akce.
 * @returns požadovaná workflow akce.
 */
export const getWorkflowActionById: Selector<IAppState, IWorkflowMenuItem | undefined, WorkflowActionSelectorParams> = (state, { dataSourceKey, workflowActionId }) =>
    getDataSourceInstance(state, dataSourceKey)?.workflowActions.actions?.[workflowActionId];

/**
 * Získá řadící parametr pro workflow akci daného záznamu
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 * @returns řadící parametr
 */
export const getWorkflowActionsOrder: Selector<IAppState, ReadonlyArray<string> | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.workflowActions.actionsOrder;

/**
 * Získá informaci, zda jsou workflow akce načítány se serveru
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 * @returns příznak, zda jsou workflow akce stále načítány
 */
export const isWorkflowLoading: Selector<IAppState, boolean, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.workflowActions.fetchingState === FetchingState.request;

/**
 * Získá **keepCheckedIncomingData** pro DataSource instanci získanou pomocí `dataSourceKey`.
 * @param state
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const keepCheckedIncomingData: Selector<IAppState, boolean | undefined, string> = (state, dataSourceKey) =>
    getDataSourceInstance(state, dataSourceKey)?.multiselect.keepCheckedIncomingData;
